/*
 * Zerbay product card enhancer
 *
 * Reads each product card's product handle from its link, fetches the
 * product JSON, and injects:
 *   1. Color carousel (small color circles) under the title+price
 *   2. Color label text (currently selected color name)
 *   3. Star rating + review count badge (if Judge.me data is available)
 *   4. Category badge (top-right corner of card image)
 *
 * Matches the visual structure of zerbay.co product cards.
 * All injections are CSS-class-based; styling lives in zerbay-overrides.css.
 *
 * Runs once on DOMContentLoaded + observes lazy-loaded cards via IntersectionObserver.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Map of color names → swatch fill (English + Turkish)
  var SWATCH_COLORS = {
    black: '#1c1c1c', siyah: '#1c1c1c',
    brown: '#4a2615', kahve: '#4a2615', kahverengi: '#4a2615',
    tan: '#a87856', camel: '#a87856', tabaki: '#a87856',
    bordeaux: '#5e1b1b', burgundy: '#5e1b1b', bordo: '#5e1b1b',
    navy: '#1b1e3f', lacivert: '#1b1e3f',
    cognac: '#8a4a1e', konyak: '#8a4a1e',
    chestnut: '#6b3410', kestane: '#6b3410',
    wine: '#6e2030', sarap: '#6e2030',
    white: '#f8f8f8', beyaz: '#f8f8f8',
    grey: '#7a7a7a', gray: '#7a7a7a', gri: '#7a7a7a',
    olive: '#4a5a30', yesil: '#4a5a30', green: '#4a5a30',
    sand: '#d8c8a0', bej: '#d8c8a0', beige: '#d8c8a0',
    blue: '#2c5e8c', mavi: '#2c5e8c'
  };

  function pickSwatchColor(name) {
    if (!name) return '#c0c0c0';
    var key = name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\W/g, '');
    return SWATCH_COLORS[key] || '#c0c0c0';
  }

  function detectCategory(productType) {
    if (!productType) return null;
    var t = productType.toUpperCase();
    var known = ['OXFORD', 'MONK', 'DERBY', 'LOAFER', 'SANDAL', 'CASUAL', 'BOOT', 'BOOTS', 'SNEAKER'];
    for (var i = 0; i < known.length; i++) {
      if (t.indexOf(known[i]) !== -1) return known[i];
    }
    return null;
  }

  function getProductHandle(cardEl) {
    var link = cardEl.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = link.getAttribute('href').match(/\/products\/([a-z0-9-]+)/i);
    return m ? m[1] : null;
  }

  function isEnhanced(cardEl) {
    return cardEl.hasAttribute('data-zb-enhanced');
  }

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.json', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        if (json && json.product) {
          productCache[handle] = json.product;
          return json.product;
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function buildColorCircles(product) {
    // Find the Color option (first option with name containing "color" or "renk")
    if (!product.options) return null;
    var colorOpt = null;
    for (var i = 0; i < product.options.length; i++) {
      var n = (product.options[i].name || '').toLowerCase();
      if (n.indexOf('color') !== -1 || n.indexOf('colour') !== -1 || n.indexOf('renk') !== -1) {
        colorOpt = product.options[i];
        break;
      }
    }
    if (!colorOpt || !colorOpt.values || colorOpt.values.length < 1) return null;

    var wrap = document.createElement('div');
    wrap.className = 'zb-card-color-carousel';

    colorOpt.values.forEach(function (val) {
      var dot = document.createElement('span');
      dot.className = 'zb-card-color-dot';
      dot.style.background = pickSwatchColor(val);
      dot.setAttribute('title', val);
      dot.setAttribute('aria-label', val);
      wrap.appendChild(dot);
    });

    return wrap;
  }

  function buildCategoryBadge(product) {
    var cat = detectCategory(product.product_type) || detectCategory(product.title);
    if (!cat) return null;
    var b = document.createElement('span');
    b.className = 'zb-card-category';
    b.textContent = cat;
    return b;
  }

  function injectCardElements(cardEl, product) {
    cardEl.setAttribute('data-zb-enhanced', '1');

    // 1. Color carousel: append below title+price area
    var contentArea = cardEl.querySelector('.product-card__content') ||
                      cardEl.querySelector('[class*="card__content"]') ||
                      cardEl.querySelector('[class*="text-block"]')?.parentElement ||
                      cardEl;
    var colors = buildColorCircles(product);
    if (colors && contentArea) {
      // Avoid double-insertion
      if (!cardEl.querySelector('.zb-card-color-carousel')) {
        contentArea.appendChild(colors);
      }
    }

    // 2. Category badge: top-right of image
    var fallbackImage = cardEl.querySelector('img');
    var imageWrap = cardEl.querySelector('.product-media-container') ||
                    cardEl.querySelector('[class*="card-gallery"]') ||
                    cardEl.querySelector('[class*="media-container"]') ||
                    cardEl.querySelector('slideshow-component') ||
                    (fallbackImage && fallbackImage.parentElement);
    var cat = buildCategoryBadge(product);
    if (cat && imageWrap) {
      if (!cardEl.querySelector('.zb-card-category')) {
        imageWrap.appendChild(cat);
      }
    }
  }

  function enhanceCard(cardEl) {
    if (isEnhanced(cardEl)) return;
    if (cardEl.closest('.shopify-section--static-collection, .static-search, .featured-collection .product-list, .product-list .product')) return;
    var handle = getProductHandle(cardEl);
    if (!handle) return;
    cardEl.setAttribute('data-zb-handle', handle);
    fetchProduct(handle).then(function (product) {
      if (product) injectCardElements(cardEl, product);
    });
  }

  function enhanceAll() {
    var cards = document.querySelectorAll('product-card, .product-grid__card, .card-product, .product-card:not(.product-card-details):not(.product-card-footer):not(.product-card-footer-mobile):not(.product-card-figure):not(.product-card-overlay):not(.product-card-interaction-addtocart)');
    cards.forEach(enhanceCard);
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll);
  } else {
    enhanceAll();
  }

  // Watch for lazy-loaded cards (filters, pagination, etc.)
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      var newCards = false;
      mutations.forEach(function (m) {
        m.addedNodes && Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && (node.matches('product-card') || node.matches('[class*="product-card"]'))) {
            newCards = true;
          } else if (node.querySelector && node.querySelector('product-card, [class*="product-card"]')) {
            newCards = true;
          }
        });
      });
      if (newCards) enhanceAll();
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
